import { forwardRef, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { IoClose } from "react-icons/io5";
import { useGlobalStore } from "~/stores/store-provider";
import { Connection } from "~/stores/slices/database";
import { TbDatabasePlus } from "react-icons/tb";
import { SiPostgresql, SiMysql, SiSnowflake, SiShopify, SiInstagram } from "react-icons/si";

interface DataSourceSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSelectedSources: string[];
  onSelectionChange?: (ids: string[]) => void;
}

const DataSourceSelectorModal = forwardRef<HTMLDialogElement, DataSourceSelectorModalProps>(
  ({ isOpen, onClose, initialSelectedSources, onSelectionChange }, ref) => {
    const navigate = useNavigate();
    const { connectionsList } = useGlobalStore((state) => state);
    const [internalSelection, setInternalSelection] = useState<string[]>(initialSelectedSources);
    const viewOnly = !onSelectionChange;

    useEffect(() => {
      setInternalSelection(initialSelectedSources);
    }, [initialSelectedSources, isOpen]);

    const handleToggle = (connectionId: string) => {
      if (!viewOnly && onSelectionChange) {
        const newSelection = internalSelection.includes(connectionId)
          ? internalSelection.filter(id => id !== connectionId)
          : [...internalSelection, connectionId];

        setInternalSelection(newSelection);
        onSelectionChange(newSelection);
      }
    };

    const isSelected = (connectionId: string) => {
      return internalSelection.includes(connectionId);
    };

    const getConnectionIcon = (type: string) => {
      switch (type) {
        case "postgresql":
          return <SiPostgresql className="text-lg" />;
        case "mysql":
          return <SiMysql className="text-lg" />;
        case "snowflake":
          return <SiSnowflake className="text-lg" />;
        case "shopify":
          return <SiShopify className="text-lg" />;
        case "instagram":
          return <SiInstagram className="text-lg" />;
        default:
          return null;
      }
    };

    return (
      <dialog ref={ref} className="modal backdrop-blur-sm" open={isOpen}>
        <div className="modal-box w-11/12 max-w-3xl bg-base-200">
          <form method="dialog">
            <button className="btn btn-ghost btn-circle btn-sm absolute right-2 top-2" onClick={onClose}>
              <IoClose size={20} />
            </button>
          </form>

          <div className="flex flex-col gap-4">
            {connectionsList.length > 0 ? (
              <>
                <div className="p-4 pb-2 text-xs opacity-60 tracking-wide">
                  {viewOnly ? 'Connected Data Sources' : 'Select Data Sources'}
                </div>

                <ul className="list bg-base-200 rounded-lg max-h-96 overflow-y-auto">
                  {[...connectionsList]
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .map((connection: Connection) => (
                    <label
                      key={connection._id}
                      className={`list-row py-4 ${!viewOnly ? 'cursor-pointer hover:bg-base-300/50' : ''} transition-colors`}
                    >
                      <div className="px-4">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-xs"
                          checked={isSelected(connection._id)}
                          onChange={() => handleToggle(connection._id)}
                          disabled={viewOnly}
                        />
                      </div>
                      <div className="px-3">
                        {getConnectionIcon(connection.connection_type)}
                      </div>
                      <div className="list-col-grow px-2">
                        <div className="uppercase">{connection.name}</div>
                        {connection.description && (
                          <div className="text-xs uppercase font-semibold opacity-60">
                            {connection.description}
                          </div>
                        )}
                      </div>
                      <div className="px-4">
                        <span className="badge badge-outline badge-sm">
                          {connection.connection_type}
                        </span>
                      </div>
                    </label>
                  ))}
                </ul>

                {/* Action buttons - only show when not in view-only mode */}
                {!viewOnly && (
                  <div
                    className="flex items-center gap-3 py-4 px-4 cursor-pointer hover:bg-base-300/50 transition-colors rounded-lg bg-base-200"
                    onClick={() => {
                      onClose();
                      navigate('/connections');
                    }}
                  >
                    <TbDatabasePlus size={20} />
                    <div>Add Data Source</div>
                  </div>
                )}

                {!viewOnly && (
                  <div className="p-4 pt-2 text-xs opacity-60">
                    To attach files, use the + button in the input area
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="p-4 pb-2 text-xs opacity-60 tracking-wide">
                  No Data Sources
                </div>

                {/* Action buttons when no connections */}
                {!viewOnly && (
                  <div
                    className="flex items-center gap-3 py-4 px-4 cursor-pointer hover:bg-base-300/50 transition-colors rounded-lg bg-base-200"
                    onClick={() => {
                      onClose();
                      navigate('/connections');
                    }}
                  >
                    <TbDatabasePlus size={20} />
                    <div>Add Data Source</div>
                  </div>
                )}

                {!viewOnly && (
                  <div className="p-4 pt-2 text-xs opacity-60">
                    To attach files, use the + button in the input area
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="modal-backdrop" onClick={onClose} />
      </dialog>
    );
  }
);

DataSourceSelectorModal.displayName = "DataSourceSelectorModal";

export default DataSourceSelectorModal;
